/// <reference path="./worker-url.d.ts" />
import React, { useEffect, useRef } from 'react';
import type L from 'leaflet';
import { useMap } from 'react-leaflet';
import { setWorkerUrl } from 'maplibre-gl';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { maplibreGL } from '@maplibre/maplibre-gl-leaflet';
import 'maplibre-gl/dist/maplibre-gl.css';

import { useIsDarkTheme } from './use-is-dark-theme';

// MapLibre finds its worker relative to its own file, which Vite's dependency
// pre-bundling moves. Let Vite bundle the worker and say where it ended up.
setWorkerUrl(maplibreWorkerUrl);

// OpenFreeMap: free vector tiles, no API key, commercial use allowed.
const styleUrl = (style: string) => `https://tiles.openfreemap.org/styles/${style}`;

/**
 * The platform's street-level basemap, following the app theme: the full-colour
 * `liberty` in light mode (roads, parks, water, 3D buildings up close), `dark`
 * in dark mode.
 *
 * Render as a child of a `<MapContainer>`. The MapLibre canvas sits in
 * Leaflet's tile pane, below every marker and overlay, and attribution is
 * forwarded to Leaflet's control.
 *
 * A theme flip calls `setStyle` on the live GL map rather than remounting the
 * layer, which would tear down and rebuild the WebGL context.
 */
export const StreetBasemap: React.FC = () => {
  const map = useMap();
  const isDark = useIsDarkTheme();
  const style = styleUrl(isDark ? 'dark' : 'liberty');
  const layerRef = useRef<L.MaplibreGL | null>(null);

  useEffect(() => {
    const layer = maplibreGL({ style });
    layer.addTo(map);
    layerRef.current = layer;
    return () => {
      layer.remove();
      layerRef.current = null;
    };
    // Mount once per map; style changes go through the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    layerRef.current?.getMaplibreMap().setStyle(style);
  }, [style]);

  return null;
};
